import React from 'react';
import PaymentStatusBadge from '../../../Common/PaymentStatusBadge';
import { Card, CardHeader } from './primitives';
import { CURRENCY, formatAmount } from '../shared/orderFormat';
import { VAT_RATE_PERCENT } from '../shared/orderConstants';
import DeliveryChargeEditor from './DeliveryChargeEditor';
import BalanceDue, { BalanceSettled } from './BalanceDue';

/*
 * What was charged, as opposed to what was ordered.
 *
 * The ladder is additive and every row is real: rows worth zero are not
 * rendered at all. The old design printed "Applied Coupon —", "Coupon Discount
 * − 0.00 SAR" and "Instant Discount − 0.00 SAR" on every order, the last two in
 * alarm red, then apologised twice with "(already applied)".
 *
 * That parenthetical existed because a discount row in an additive column reads
 * as one more thing to subtract, when final_price is already post-discount. The
 * middle context column solves it properly: the row states its own basis
 * ("RAMADAN20 −20%") and needs no apology.
 *
 * Items Subtotal + Delivery = Order Total holds by construction, because the
 * merchandise figure is recovered by removing delivery from the total rather
 * than summed from the rows. Same identity the PDF invoice prints.
 */

const LadderRow = ({ label, context, amount, tone = 'default' }) => (
  <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-baseline py-1">
    <span className="text-[12.5px] font-medium text-[#6B7C86]">{label}</span>
    <span className="text-[11px] font-medium text-[#96A5AD] text-right">{context}</span>
    <span
      className={`text-[13px] font-semibold text-right tabular-nums min-w-[78px] ${
        tone === 'danger' ? 'text-[#B23A32]' : 'text-[#3B4A54]'
      }`}
    >
      {amount}
    </span>
  </div>
);

const PaymentCard = ({
  order,
  merchandiseSubtotal,
  deliveryCharge,
  itemCount,
  couponDiscount,
  instantDiscount,
  canEditContents = false,
  isDeliveryPending = false,
  onDeliveryChargeChange,
}) => {
  return (
    <Card>
      <CardHeader
        title="Payment"
        action={<PaymentStatusBadge value={order.payment_status} />}
      />
      <div className="px-3.5 py-3">
        <LadderRow
          label="Subtotal"
          context={`${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
          amount={formatAmount(merchandiseSubtotal)}
        />

        {/* Always rendered, including at zero. "Free" is a fact someone
            checking a delivery complaint needs; a missing row just looks like
            the figure failed to load. The fee is manual — the backend never
            sets it — so on an editable order this is an input, with the
            standard tier rate offered beside it as a suggestion. */}
        <div className="grid grid-cols-[1fr_auto] gap-3 items-center py-1">
          <span className="text-[12.5px] font-medium text-[#6B7C86]">
            Delivery
          </span>
          <DeliveryChargeEditor
            value={deliveryCharge}
            suggested={order.suggested_delivery_charge}
            onSave={onDeliveryChargeChange}
            isPending={isDeliveryPending}
            disabled={!canEditContents}
          />
        </div>

        {couponDiscount > 0 && (
          <LadderRow
            label="Coupon"
            context={order.coupon_code || 'applied'}
            amount={`−${formatAmount(couponDiscount)}`}
            tone="danger"
          />
        )}

        {instantDiscount > 0 && (
          <LadderRow
            label="Instant discount"
            context="applied"
            amount={`−${formatAmount(instantDiscount)}`}
            tone="danger"
          />
        )}

        <div className="grid grid-cols-[1fr_auto] gap-3 items-baseline border-t border-[#E1E7EA] mt-1.5 pt-2.5">
          <span className="text-[13px] font-bold text-nysmah-dark">
            Total
          </span>
          <span className="text-lg font-bold text-nysmah-dark tabular-nums leading-none">
            {CURRENCY} {formatAmount(order.final_price)}
          </span>
        </div>

        {/* The VAT figure stays on screen: it is the number bookkeeping
            reconciles against. The customer's invoice states the rate only. */}
        <div className="flex justify-between text-[11px] font-medium text-[#7B8A93] tabular-nums mt-1.5">
          <span>incl. {VAT_RATE_PERCENT}% VAT</span>
          <span>{formatAmount(order.vat)}</span>
        </div>

        {/* The gap between what the order is now worth and what was actually
            paid. Never folded into the ladder above — that adds up to the
            order's value; this is about money that has already moved. */}
        {order.balance_due != null && Number(order.balance_due) !== 0 ? (
          <BalanceDue value={order.balance_due} />
        ) : (
          canEditContents &&
          order.balance_due != null && <BalanceSettled />
        )}
      </div>
    </Card>
  );
};

export default PaymentCard;
